"use client";

import { useCallback, useEffect, useMemo } from "react";
import { atom, useAtom } from "jotai";
import { toast } from "sonner";
import { addDays, isAfter, isBefore, isSameDay, isWithinInterval } from "date-fns";

import { Tweet } from "@constructr-sdk/entities";
import { trpcReactQueryApi } from "~/trpc/react";
import type {
  UseTweetHistoryParams,
  UseTweetHistoryReturn,
} from "~/modules/main/hooks/use-tweet-history/use-tweet-history.interface";

// Global state atoms - hoisted to top level to prevent recreation
const searchQueryAtom = atom<string>("");
const dateFromAtom = atom<Date | null>(null);
const dateToAtom = atom<Date | null>(null);
const selectedIdsAtom = atom<Set<string>>(new Set<string>());
const editingTweetIdAtom = atom<string | null>(null);
const editingValueAtom = atom<string>("");
const lastDeletedSnapshotAtom = atom<{
  id: string;
  entity: Tweet;
  expiresAt: Date;
} | null>(null);
const isExportingAtom = atom<boolean>(false);
const isCopyingAtom = atom<boolean>(false);
const isSavingAtom = atom<boolean>(false);
const isDeletingAtom = atom<boolean>(false);

export function useTweetHistory(
  params: UseTweetHistoryParams,
): UseTweetHistoryReturn {
  // Initialize state from params
  const [searchQuery, setSearchQueryState] = useAtom(searchQueryAtom);
  const [dateFrom, setDateFromState] = useAtom(dateFromAtom);
  const [dateTo, setDateToState] = useAtom(dateToAtom);
  const [selectedIds, setSelectedIdsState] = useAtom(selectedIdsAtom);
  const [editingTweetId, setEditingTweetIdState] = useAtom(editingTweetIdAtom);
  const [editingValue, setEditingValueState] = useAtom(editingValueAtom);
  const [lastDeletedSnapshot, setLastDeletedSnapshotState] = useAtom(
    lastDeletedSnapshotAtom,
  );
  const [isExporting, setIsExporting] = useAtom(isExportingAtom);
  const [isCopying, setIsCopying] = useAtom(isCopyingAtom);
  const [isSaving, setIsSaving] = useAtom(isSavingAtom);
  const [isDeleting, setIsDeleting] = useAtom(isDeletingAtom);

  // Initialize filters from params on mount
  useEffect(() => {
    if (params.initialSearchQuery !== undefined) {
      setSearchQueryState(params.initialSearchQuery);
    }
    if (params.initialDateFrom !== undefined) {
      setDateFromState(params.initialDateFrom);
    }
    if (params.initialDateTo !== undefined) {
      setDateToState(params.initialDateTo);
    }
  }, []); // Empty deps - only run on mount

  // TRPC utils for cache invalidation
  const utils = trpcReactQueryApi.useUtils();

  // Fetch tweets with server-side filtering
  const {
    data: tweetsData,
    isLoading,
    error: tweetsError,
  } = trpcReactQueryApi.main.tweetHistory.all.useQuery({
    searchQuery: searchQuery || undefined,
    dateFrom: dateFrom ?? undefined,
    dateTo: dateTo ?? undefined,
  });

  const tweets = tweetsData ?? [];

  // Client-side filtering for real-time updates
  const filteredTweets = useMemo(() => {
    let filtered = [...tweets];

    // Apply search filter
    if (searchQuery.trim().length > 0) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (tweet) =>
          tweet.content.toLowerCase().includes(lowerQuery) ||
          tweet.topic.toLowerCase().includes(lowerQuery) ||
          tweet.audience.toLowerCase().includes(lowerQuery) ||
          (tweet.keywords?.toLowerCase().includes(lowerQuery) ?? false),
      );
    }

    // Apply date range filter
    if (dateFrom || dateTo) {
      filtered = filtered.filter((tweet) => {
        const tweetDate = tweet.createdAt;
        if (dateFrom && dateTo) {
          return isWithinInterval(tweetDate, { start: dateFrom, end: dateTo });
        } else if (dateFrom) {
          return isAfter(tweetDate, dateFrom) || isSameDay(tweetDate, dateFrom);
        } else if (dateTo) {
          return isBefore(tweetDate, dateTo) || isSameDay(tweetDate, dateTo);
        }
        return true;
      });
    }

    return filtered;
  }, [tweets, searchQuery, dateFrom, dateTo]);

  // Derive areAllVisibleSelected
  const areAllVisibleSelected = useMemo(() => {
    if (filteredTweets.length === 0) return false;
    return filteredTweets.every((tweet) => selectedIds.has(tweet.id));
  }, [filteredTweets, selectedIds]);

  // Update mutations
  const updateMutation = trpcReactQueryApi.main.tweetHistory.update.useMutation({
    onSuccess: async () => {
      toast.success("Tweet updated successfully");
      await utils.main.tweetHistory.all.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update tweet");
    },
  });

  const deleteMutation = trpcReactQueryApi.main.tweetHistory.delete.useMutation({
    onSuccess: async () => {
      toast.success("Tweet deleted successfully");
      await utils.main.tweetHistory.all.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete tweet");
    },
  });

  const restoreMutation = trpcReactQueryApi.main.tweetHistory.restore.useMutation({
    onSuccess: async () => {
      toast.success("Tweet restored successfully");
      await utils.main.tweetHistory.all.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to restore tweet");
    },
  });

  // Mutations
  const setSearchQuery = useCallback(
    (query: string) => {
      setSearchQueryState(query);
    },
    [setSearchQueryState],
  );

  const setDateFrom = useCallback(
    (from: Date | null) => {
      setDateFromState(from);
    },
    [setDateFromState],
  );

  const setDateTo = useCallback(
    (to: Date | null) => {
      setDateToState(to);
    },
    [setDateToState],
  );

  const toggleSelect = useCallback(
    (tweetId: string) => {
      setSelectedIdsState((prev: Set<string>) => {
        const newSet = new Set(prev);
        if (newSet.has(tweetId)) {
          newSet.delete(tweetId);
        } else {
          newSet.add(tweetId);
        }
        return newSet;
      });
    },
    [setSelectedIdsState],
  );

  const setSelectAllVisible = useCallback(
    (select: boolean) => {
      setSelectedIdsState((prev: Set<string>) => {
        const newSet = new Set(prev);
        filteredTweets.forEach((tweet) => {
          if (select) {
            newSet.add(tweet.id);
          } else {
            newSet.delete(tweet.id);
          }
        });
        return newSet;
      });
    },
    [filteredTweets, setSelectedIdsState],
  );

  const clearSelection = useCallback(() => {
    setSelectedIdsState(new Set());
  }, [setSelectedIdsState]);

  const startEditing = useCallback(
    (tweetId: string, initialValue: string) => {
      setEditingTweetIdState(tweetId);
      setEditingValueState(initialValue);
    },
    [setEditingTweetIdState, setEditingValueState],
  );

  const setEditingValue = useCallback(
    (value: string) => {
      // Enforce 280 character limit
      if (value.length <= 280) {
        setEditingValueState(value);
      }
    },
    [setEditingValueState],
  );

  const cancelEditing = useCallback(() => {
    setEditingTweetIdState(null);
    setEditingValueState("");
  }, [setEditingTweetIdState, setEditingValueState]);

  // Commands
  const loadTweets = useCallback(async () => {
    await utils.main.tweetHistory.all.invalidate();
  }, [utils]);

  const saveEditing = useCallback(
    async (tweetId: string, newContent: string) => {
      if (newContent.trim().length === 0) {
        toast.error("Tweet content cannot be empty");
        return;
      }
      if (newContent.length > 280) {
        toast.error("Tweet must be 280 characters or less");
        return;
      }

      setIsSaving(true);
      try {
        await updateMutation.mutateAsync({
          id: tweetId,
          content: newContent,
        });
        cancelEditing();
      } finally {
        setIsSaving(false);
      }
    },
    [updateMutation, cancelEditing, setIsSaving],
  );

  const copyTweet = useCallback(
    async (tweetId: string) => {
      const tweet = tweets.find((t) => t.id === tweetId);
      if (!tweet) {
        toast.error("Tweet not found");
        return;
      }

      setIsCopying(true);
      try {
        await navigator.clipboard.writeText(tweet.content);
        toast.success("Tweet copied to clipboard");
      } catch (error) {
        toast.error("Failed to copy tweet to clipboard");
      } finally {
        setIsCopying(false);
      }
    },
    [tweets, setIsCopying],
  );

  const deleteTweet = useCallback(
    async (tweetId: string) => {
      const tweet = tweets.find((t) => t.id === tweetId);
      if (!tweet) {
        toast.error("Tweet not found");
        return;
      }

      // Store snapshot for undo (expires in 10 seconds)
      const expiresAt = addDays(new Date(), 0);
      expiresAt.setSeconds(expiresAt.getSeconds() + 10);
      setLastDeletedSnapshotState({
        id: tweet.id,
        entity: tweet,
        expiresAt: expiresAt,
      });

      setIsDeleting(true);
      try {
        await deleteMutation.mutateAsync({ id: tweetId });
        // Clear selection if deleted tweet was selected
        setSelectedIdsState((prev: Set<string>) => {
          const newSet = new Set(prev);
          newSet.delete(tweetId);
          return newSet;
        });
      } finally {
        setIsDeleting(false);
      }
    },
    [tweets, deleteMutation, setLastDeletedSnapshotState, setSelectedIdsState, setIsDeleting],
  );

  const undoDelete = useCallback(async () => {
    if (!lastDeletedSnapshot) {
      toast.error("No recent deletion to undo");
      return;
    }

    // Check if undo has expired
    if (isAfter(new Date(), lastDeletedSnapshot.expiresAt)) {
      toast.error("Undo period has expired");
      setLastDeletedSnapshotState(null);
      return;
    }

    try {
      await restoreMutation.mutateAsync({
        id: lastDeletedSnapshot.entity.id,
        userId: lastDeletedSnapshot.entity.userId,
        topic: lastDeletedSnapshot.entity.topic,
        tone: lastDeletedSnapshot.entity.tone,
        audience: lastDeletedSnapshot.entity.audience,
        keywords: lastDeletedSnapshot.entity.keywords,
        length: lastDeletedSnapshot.entity.length,
        content: lastDeletedSnapshot.entity.content,
        charCount: lastDeletedSnapshot.entity.charCount,
        createdAt: lastDeletedSnapshot.entity.createdAt,
        updatedAt: lastDeletedSnapshot.entity.updatedAt,
      });
      setLastDeletedSnapshotState(null);
    } catch (error) {
      toast.error("Failed to restore tweet");
    }
  }, [lastDeletedSnapshot, restoreMutation, setLastDeletedSnapshotState]);

  const exportSelectedToCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      // Determine which tweets to export
      const tweetsToExport =
        selectedIds.size > 0
          ? filteredTweets.filter((tweet) => selectedIds.has(tweet.id))
          : filteredTweets;

      if (tweetsToExport.length === 0) {
        toast.error("No tweets to export");
        return;
      }

      // Generate CSV content
      const headers = [
        "ID",
        "Topic",
        "Tone",
        "Audience",
        "Keywords",
        "Length",
        "Content",
        "Character Count",
        "Created At",
        "Updated At",
      ];
      const csvRows = [headers.join(",")];

      tweetsToExport.forEach((tweet) => {
        const row = [
          tweet.id,
          `"${tweet.topic.replace(/"/g, '""')}"`,
          tweet.tone,
          `"${tweet.audience.replace(/"/g, '""')}"`,
          tweet.keywords ? `"${tweet.keywords.replace(/"/g, '""')}"` : "",
          tweet.length,
          `"${tweet.content.replace(/"/g, '""')}"`,
          tweet.charCount.toString(),
          tweet.createdAt.toISOString(),
          tweet.updatedAt ? tweet.updatedAt.toISOString() : "",
        ];
        csvRows.push(row.join(","));
      });

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tweets_export_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${tweetsToExport.length} tweets to CSV`);
    } catch (error) {
      toast.error("Failed to export tweets");
    } finally {
      setIsExporting(false);
    }
  }, [selectedIds, filteredTweets, setIsExporting]);

  return {
    // State
    isLoading,
    error: tweetsError ? tweetsError.message : null,
    tweets,
    filteredTweets,
    searchQuery,
    dateFrom,
    dateTo,
    selectedIds,
    areAllVisibleSelected,
    editingTweetId,
    editingValue,
    lastDeletedSnapshot,
    isExporting,
    isCopying,
    isSaving,
    isDeleting,

    // Mutations
    setSearchQuery,
    setDateFrom,
    setDateTo,
    toggleSelect,
    setSelectAllVisible,
    clearSelection,
    startEditing,
    setEditingValue,
    cancelEditing,

    // Commands
    loadTweets,
    saveEditing,
    copyTweet,
    deleteTweet,
    undoDelete,
    exportSelectedToCsv,
  };
}
